import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { Check, ArrowRight, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_no-sub-pdf/artifacts/kribzoad_LogoPDFeditor.png";

const ctaLabels = {
  en: 'Get Early Access',
  fr: 'Accès anticipé',
  es: 'Acceso anticipado',
  de: 'Frühzugang',
  it: 'Accesso anticipato',
  pt: 'Acesso antecipado',
  nl: 'Vroege toegang',
  pl: 'Wczesny dostęp',
  tr: 'Erken erişim',
  ru: 'Ранний доступ',
  ja: '早期アクセス',
  zh: '抢先体验',
  ar: 'وصول مبكر',
};

export const HeroSection = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const bullets = t('hero.bullets');
  const priceMap = { eur: '4,99\u20ac', usd: '$4.99', gbp: '\u00a34.34', chf: 'CHF4.53', cad: 'C$7.92', jpy: '\u00a5913', aud: 'A$8.26' };
  const price = priceMap[currency] || priceMap.eur;
  const ctaText = `${ctaLabels[lang] || ctaLabels.en} (${price})`;

  const handleEarlyAccess = () => {
    if (user) {
      navigate('/pricing');
    } else {
      navigate('/login', { state: { redirect: '/pricing', message: 'signin_to_buy' } });
    }
  };

  return (
    <section className="relative flex items-center pt-14 sm:pt-16 pb-4 sm:pb-8 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-background" />
      <div className="absolute top-0 right-0 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500/5 via-transparent to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full py-8 sm:py-0">
        <div className="grid md:grid-cols-2 gap-8 md:gap-20 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs sm:text-sm font-medium mb-6 sm:mb-8" data-testid="hero-badge">
              <Zap className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              {t('hero.badge')}
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl md:text-7xl font-bold tracking-tight text-foreground leading-[1.1] mb-4 sm:mb-6" data-testid="hero-title">
              {t('hero.h1_line1')}<br />
              {t('hero.h1_line2')}<br />
              <span className="text-blue-500">{t('hero.h1_accent')}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed mb-6 sm:mb-8 max-w-lg" data-testid="hero-subtitle">
              {t('hero.subtitle')}
            </p>
            <ul className="space-y-2.5 sm:space-y-3 mb-8 sm:mb-10">
              {Array.isArray(bullets) && bullets.map((b, i) => (
                <motion.li key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2.5 sm:gap-3 text-sm sm:text-base text-foreground/80" data-testid={`hero-bullet-${i}`}>
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  {b}
                </motion.li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4">
              <Button
                className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 sm:px-8 py-3 h-auto text-sm sm:text-base font-medium shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all w-full sm:w-auto"
                onClick={handleEarlyAccess}
                data-testid="hero-cta-primary"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('hero.microtext')}</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="hidden md:flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-blue-500/20 blur-[80px] rounded-full" />
              <div className="relative p-8 rounded-3xl bg-muted/50 backdrop-blur-md border border-border">
                <img src={LOGO_ICON} alt="HexoPDF" className="w-48 h-48 md:w-64 md:h-64" data-testid="hero-logo" />
                <div className="absolute -top-3 -right-3 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                  {t('hero.tag_ocr')}
                </div>
                <div className="absolute -bottom-3 -left-3 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs font-medium">
                  {t('hero.tag_cloud')}
                </div>
                <div className="absolute -bottom-3 -right-3 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 text-blue-600 dark:text-blue-400 text-xs font-medium">
                  {t('hero.tag_pay')}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
